import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import {
  Grid,
  IconGraphy,
  Tab,
  Typography,
  Tooltip,
  colors,
  Table,
  Badge,
  Dropdown,
  Loading,
} from '@leapeasy/ui-kit';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { openDetails } from 'store/actions/uiActions';
import styled from 'styled-components';
import { getBuilding } from 'store/sagas/APIs/user';
import { ListExportScopeModal } from 'components/Tables/shared/ListExportScopeModal';
import { fetchAllPaginated } from 'utils/exportPagination';
import { GlobalUXState } from 'components/GlobalUXState';
import { buildMaskedUserBuildingRow } from 'utils/demoMask';
import { useImmutableJs } from 'hooks/useImmutableJs';
import { useBuildingListFetch } from 'hooks/useBuildingListFetch';
import { fitContentTextColumnOptions } from 'utils/fitContentTextColumnOptions';
import { getDelegationPropertyId } from 'utils/delegationPropertyId';
import { BOOLEAN_YES_NO_OPTIONS, formatBooleanYesNo } from 'utils/formatBooleanYesNo';

export const MyBuildingTable = ({ route, dropFilterActiveCount, dropFilterActiveSummary }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [tableData, setTableData] = useState([]);
  const [filter, setFilter] = useState({
    options: {},
    searchText: '',
    searchPlaceholder: 'Search Property..',
  });
  const [selectedBType, selSelectedBType] = useState([]);
  const [isSHouse, setIsSHouse] = useState([]);

  const [sortOptions, setSortOptions] = useState({});
  const [pagination, setPagination] = useState({
    pageNumber: 0,
    rowsPerPage: 12,
    maxVisiblePageItems: 6,
    onUpdate: (value) => {
      if (value === -1) value = 0;
      setPagination((prev) => ({ ...prev, pageNumber: value }));
    },
    showPageNumberInput: 6,
  });
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const tableRef = useRef(null);
  const [fullExportDatasetTotal, setFullExportDatasetTotal] = useState(undefined);

  const buildings = useSelector((state) => state.getIn(['property', 'building']));
  const buildingsJSON = useImmutableJs(buildings);
  const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));
  const details = useSelector((state) => state.getIn(['ui', 'details'])) || null;
  const detailsJSON = useImmutableJs(details);
  const detailsData = details?.get?.('data') ?? null;
  const propertyId = useMemo(() => getDelegationPropertyId(detailsData), [detailsData]);

  useEffect(() => {
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [filter.searchText]);

  useBuildingListFetch({
    propertyId,
    filter,
    pagination,
    sortOptions,
    selectedBType,
    isSHouse,
    enabled: Boolean(propertyId),
  });

  const onClickBack = useCallback(() => {
    navigate(-1);
    // dispatch(openDetails(null));
  }, []);

  useEffect(() => {
    const data = [];
    if (buildings) {
      (buildings.toJS()?.data ?? []).forEach((building, value) => {
        data.push(buildMaskedUserBuildingRow(building, isDemo, value));
      });
    }
    setTableData(data);
  }, [buildings, isDemo]);

  const buildBuildingRow = (building, value) => buildMaskedUserBuildingRow(building, isDemo, value);

  useEffect(() => {
    const pid = detailsJSON?.data?.property;
    if (!pid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getBuilding({
          id: pid,
          filter: { options: {}, searchText: '', searchPlaceholder: 'Search Property..' },
          offset: 0,
          limit: 1,
          sortOptions,
          selectedBType: [],
          isSHouse: [],
        });
        if (!cancelled && response?.total != null) {
          setFullExportDatasetTotal(Number(response.total));
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detailsJSON?.data?.property, sortOptions]);

  const handleRequestExportData = useCallback(async () => {
    const pid = detailsJSON?.data?.property;
    const totalRecords = Number(buildingsJSON?.total) || 0;
    if (!pid || totalRecords === 0) return [];
    try {
      const rows = await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getBuilding({
            id: pid,
            filter,
            offset,
            limit,
            sortOptions,
            selectedBType,
            isSHouse,
          }),
      });
      return rows.map((building, index) => buildBuildingRow(building, index));
    } catch (e) {
      throw new Error('Unable to export filtered properties right now.');
    }
  }, [
    detailsJSON?.data?.property,
    buildingsJSON?.total,
    filter,
    sortOptions,
    selectedBType,
    isSHouse,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const pid = detailsJSON?.data?.property;
    const totalRecords = Number(fullExportDatasetTotal) || Number(buildingsJSON?.total) || 0;
    if (!pid || totalRecords === 0) return [];
    try {
      const rows = await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getBuilding({
            id: pid,
            filter: { options: {}, searchText: '', searchPlaceholder: 'Search Property..' },
            offset,
            limit,
            sortOptions,
            selectedBType: [],
            isSHouse: [],
          }),
      });
      return rows.map((building, index) => buildBuildingRow(building, index));
    } catch (e) {
      throw new Error('Unable to export all properties right now.');
    }
  }, [detailsJSON?.data?.property, fullExportDatasetTotal, buildingsJSON?.total, sortOptions]);

  const resetFunc = () => {};
  const applyFunc = () => {};

  const user = details && detailsJSON.data;

  return (
    <Box>
      {buildings ? (
        <React.Fragment>
          <CustomBox>
            {buildingsJSON.isLoading && (
              <LoadingEffect>
                <Loading size={'large'} />
              </LoadingEffect>
            )}
            <Table
              showEmptyState={!buildingsJSON.isLoading}
              emptyContent={
                <GlobalUXState
                  variant={filter?.searchText ? 'noResults' : 'noData'}
                  compact
                  description={
                    filter?.searchText
                      ? 'No properties match your search.'
                      : 'No properties available for this user.'
                  }
                />
              }
              ref={tableRef}
              onExportButtonClick={() => setExportModalOpen(true)}
              onRequestExportData={handleRequestExportData}
              onRequestFullExportData={handleRequestFullExportData}
              data={tableData}
              autoTooltipForDefaultCells
              filter={filter}
              onChangeFilter={setFilter}
              onChangeRowsPerPage={(value) =>
                setPagination({
                  ...pagination,
                  rowsPerPage: value,
                  pageNumber: 0,
                })
              }
              dropFilter={{
                component: (
                  <DropFilterContainer>
                    <Dropdown
                      onChange={(e) => {
                        selSelectedBType(e.target.value);
                      }}
                      options={[
                        {
                          text: 'Guarantor Waiver+LDR',
                          value: 'Event Process;Auto Enroll',
                        },
                        {
                          text: 'Rent Guaranty',
                          value: 'Auto Enroll',
                        },
                      ]}
                      value={selectedBType}
                      multiselect={true}
                      showSearch={false}
                      placeholder="Property Type"
                      label="Property Type"
                      style={{
                        width: '100%',
                      }}
                    />

                    <Dropdown
                      onChange={(e) => {
                        setIsSHouse(e.target.value);
                      }}
                      options={BOOLEAN_YES_NO_OPTIONS}
                      value={isSHouse}
                      multiselect={true}
                      showSearch={false}
                      placeholder="Student Housing"
                      label="Student Housing"
                      style={{
                        width: '100%',
                      }}
                    />
                  </DropFilterContainer>
                ),
                resetFunc: resetFunc,
                applyFunc: applyFunc,
              }}
              dropFilterActiveCount={dropFilterActiveCount}
              dropFilterActiveSummary={dropFilterActiveSummary}
              pagination={{
                ...pagination,
                totalItems: buildingsJSON.total,
                totalPages: Math.ceil(buildingsJSON.total / pagination.rowsPerPage),
              }}
              onChangePagination={setPagination}
              sortOptions={sortOptions}
              onChangeSort={setSortOptions}
              style={{ width: '100%' }}
              title="My Properties"
              columns={[
                {
                  name: 'Name',
                  options: {
                    ...fitContentTextColumnOptions(),
                    sort: true,
                    customBodyRenderer: (value, _) => (
                      <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                        {value}
                      </CustomTypography>
                    ),
                  },
                  key: 'name',
                },
                {
                  name: 'Property Type',
                  options: {
                    shrink: false,
                    flexGrow: 1,
                    customBodyRenderer: (value) => {
                      const displayLabel = value != null && value !== '' ? String(value) : '—';
                      return (
                        <Tooltip title={displayLabel} placement="top" showCarot>
                          <div>
                            <Badge
                              background="rgba(243, 241, 244, 1)"
                              color={value ? 'purple' : 'tomato'}
                              label={displayLabel}
                              rounded
                              textSize="medium"
                            />
                          </div>
                        </Tooltip>
                      );
                    },
                    sort: true,
                  },
                  key: 'building_type',
                },
                {
                  name: 'Phone Number',
                  options: {
                    sort: true,
                    customBodyRenderer: (value, _) => (
                      <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                        {value}
                      </CustomTypography>
                    ),
                  },
                  key: 'phone',
                },
                {
                  name: 'Address',
                  options: {
                    flex: '170px 1 1',
                    sort: true,
                    customBodyRenderer: (value, _) => (
                      <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                        {value}
                      </CustomTypography>
                    ),
                  },
                  key: 'billingStreet',
                },
                {
                  name: 'Email Address',
                  options: {
                    flex: '120px 1 1',
                    sort: true,
                    customBodyRenderer: (value, _) => (
                      <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                        {value}
                      </CustomTypography>
                    ),
                  },
                  key: 'email_address',
                },
                {
                  name: 'Student Housing',
                  options: {
                    flex: '40px 1 1',
                    filter: true,
                    sort: true,
                    customBodyRenderer: (value) => (
                      <Badge
                        background="#F3F1F4"
                        color={value === 'true' ? 'parisGreen' : 'tomato'}
                        label={formatBooleanYesNo(value)}
                        rounded
                        textSize="medium"
                      />
                    ),
                    filterOptions: BOOLEAN_YES_NO_OPTIONS,
                  },
                  key: 'student_housing',
                },
                {
                  name: 'Detail',
                  options: {
                    flex: '5px 1 1',
                    sort: true,
                    customBodyRenderer: (value, tableMeta) => (
                      <Tooltip title="View property details" placement="top" showCarot>
                        <div>
                          <IconGraphy
                            icon={'FileFolder.Description'}
                            style={{ color: '#702572' }}
                            onClick={() => {}}
                          />
                        </div>
                      </Tooltip>
                    ),
                  },
                  key: 'detail',
                },
              ]}
            />
            <ListExportScopeModal
              open={exportModalOpen}
              onClose={() => setExportModalOpen(false)}
              tableRef={tableRef}
              title="My Properties"
              currentPageRowCount={tableData.length}
              paginationTotalItems={buildingsJSON.total}
              fullDatasetTotal={fullExportDatasetTotal}
              enableFiltered
              enableFull
              enableSelected={false}
            />
          </CustomBox>
        </React.Fragment>
      ) : (
        <Loading size={'large'} />
      )}
    </Box>
  );
};

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const CustomBox = styled.div`
  position: relative;
`;

const LoadingEffect = styled.div`
  position: absolute;
  top: 35%;
  left: 45%;
  z-index: 1;
`;

const CustomTypography = styled(Typography)`
  word-break: normal;
  overflow-wrap: break-word;
`;
